import { CapacityHistory } from '../features/history/PageHistory.tsx';
import { Boxes, Clock, Gauge as GaugeIcon, ListOrdered, Plus, RotateCw, Server } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { Banner, Button, MeterBar, Panel, RingMeter, Segmented, Skeleton, SkeletonRows, StatStrip, StatTile, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, formatBytes, formatInt, plural, useNow } from '@orgus/ui';
import { StatStripSkeleton } from '../features/fleet/StatStrip.tsx';
import { PageVerdict } from '../features/glance/PageVerdict.tsx';
import { capacityVerdict } from '../features/scheduler/verdict.ts';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useOrgus, useMachines, useProjects } from '../lib/store.ts';
import { useMissionsState } from '../features/missions/data.ts';
import { CapacityTable, SlotLegend } from '../features/scheduler/CapacityTable.tsx';
import { PoolsTab } from '../features/scheduler/PoolsTab.tsx';
import { QueueDialog } from '../features/scheduler/QueueDialog.tsx';
import { QueueList } from '../features/scheduler/QueueList.tsx';
import { RulesTab } from '../features/scheduler/RulesTab.tsx';
import { useLiveCapacity, useQueue, useSchedulerState } from '../features/scheduler/data.ts';
import { capacityTotals, fleetWirePerMinute } from '../features/scheduler/model.ts';
import { ModuleAbsent } from '../features/server/ModuleAbsent.tsx';

type Tab = 'machines' | 'rules' | 'pools';

/** Tab icons give way on a phone, so the three tabs and their counts fit one row. */
const TAB_ICON = 'hidden sm:block';

function CapacitySkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading capacity">
      <StatStripSkeleton cells={4} />
      <Skeleton className="h-row w-80" />
      <div className="rounded-card border border-border bg-surface">
        <SkeletonRows rows={4} />
      </div>
    </div>
  );
}

/**
 * Capacity: who is using which machine, what waits for room and why, and the rules and pools
 * that decide where missions run.
 *   /capacity                 machines and the queue (?queue=history for what already ran)
 *   /capacity?tab=rules       time rules, limits, headroom, per-project offline policy
 *   /capacity?tab=pools       node pools and their machines
 */
export default function Capacity() {
  const now = useNow(5000);
  const [params, setParams] = useSearchParams();
  const tabRaw = params.get('tab');
  const tab: Tab = tabRaw === 'rules' || tabRaw === 'pools' ? tabRaw : 'machines';
  const history = params.get('queue') === 'history';
  const { state, phase } = useSchedulerState();
  const capacity = useLiveCapacity(state, now);
  const queue = useQueue(state, now);
  const machinesList = useMachines();
  const projectsList = useProjects();
  const { machines, projects } = useOrgus(useShallow((s) => ({ machines: s.machines, projects: s.projects })));
  const missionsState = useMissionsState().state;
  const missions = useMemo(() => Object.fromEntries((missionsState?.missions ?? []).map((m) => [m.id, m])), [missionsState]);
  const [queueing, setQueueing] = useState(false);
  const navigate = useNavigate();

  const setParam = (k: string, v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );

  const cmds = useMemo<Command[]>(
    () => [
      { id: 'scheduler.queue', title: 'Queue mission', subtitle: 'Start on the first machine that fits', group: 'Actions', icon: ListOrdered, order: 15, perform: () => setQueueing(true) },
      { id: 'scheduler.rules', title: 'Scheduling rules and limits', group: 'Navigation', icon: Clock, perform: () => setParam('tab', 'rules') },
      { id: 'scheduler.pools', title: 'Node pools', group: 'Navigation', icon: Boxes, perform: () => setParam('tab', 'pools') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useRegisterCommands(cmds, [cmds]);

  const waiting = queue.filter((e) => e.state === 'queued' || e.state === 'starting');
  const done = queue.filter((e) => e.state !== 'queued' && e.state !== 'starting');
  const totals = capacityTotals(capacity, queue);
  const offline = totals.machines - totals.online;
  // ADR 0104: the fleet's wire cost, from each node's own counters (nothing for older agents).
  const fleetWire = fleetWirePerMinute(machinesList, now);

  return (
    <PageBody wide>
      <PageActions>
        {state && (
          <Tooltip content="Start a mission on the first machine that fits">
            <Button variant="secondary" size="sm" icon={<Plus className="text-accent-text" />} onClick={() => setQueueing(true)}>
              Queue mission
            </Button>
          </Tooltip>
        )}
      </PageActions>
      <DataGate skeleton={<CapacitySkeleton />}>
        {phase === 'unavailable' ? (
          <div className="rounded-card border border-border bg-surface">
            <ModuleAbsent
              size="page"
              icon={<GaugeIcon />}
              feature="Mission scheduling"
              hub={{ title: 'This hub does not schedule missions yet', description: 'Update Orgus on the hub machine, then reload' }}
              instead="Until then, missions start as soon as you launch them. Machines shows each machine's load."
            />
          </div>
        ) : !state ? (
          <CapacitySkeleton />
        ) : (
          <div className="flex flex-col gap-6">
            {/* ORGU-154: a pending hub restart holds every start but red-main fixes until its drain empties. */}
            {state.restartPending && (
              <Banner tone="attention" icon={<RotateCw />} title="Hub restart pending">
                Waits for {plural(state.restartPending.waitsFor, 'session')} to end. Only red-main fixes start until then.
              </Banner>
            )}
            <PageVerdict
              v={capacityVerdict(totals, !!state.restartPending)}
              action={
                totals.queued > 0 && totals.room === 0 ? (
                  <Button variant="secondary" size="sm" onClick={() => setParam('tab', 'rules')}>
                    Open rules
                  </Button>
                ) : undefined
              }
            />
            <StatStrip label="Capacity now" className="lg:grid-cols-4">
              <StatTile
                label="Room now"
                featured
                value={totals.room}
                unit={totals.room === 1 ? 'session' : 'sessions'}
                sub="By CPU and memory"
                trend={<RingMeter value={totals.used + totals.room ? totals.room / (totals.used + totals.room) : 0} size={40} tone={3} label={`Room for ${formatInt(totals.room)} more sessions`} />}
              />
              {totals.work.machines ? (
                <StatTile
                  label="Heavy work"
                  value={totals.work.used}
                  of={totals.work.budget}
                  sub={totals.work.waiting ? `${formatInt(totals.work.waiting)} wait for CPU` : plural(totals.used, 'session')}
                  visual={<MeterBar value={totals.work.budget ? totals.work.used / totals.work.budget : 0} tone={totals.work.waiting ? 'attention' : 2} label={`${formatInt(totals.work.used)} of ${formatInt(totals.work.budget)} heavy work slots in use`} />}
                />
              ) : (
                <StatTile label="Running" value={totals.used} unit={totals.used === 1 ? 'session' : 'sessions'} sub="No load reports" />
              )}
              <StatTile label="Waiting" value={totals.queued} unit={totals.queued === 1 ? 'mission' : 'missions'} attention={totals.queued > 0} quietAtZero sub={totals.queued ? 'Start on their own' : 'None waiting'} />
              <StatTile
                label="Machines online"
                value={totals.online}
                of={totals.machines}
                sub={totals.drained ? `${formatInt(totals.drained)} drained` : 'None drained'}
                trend={<RingMeter value={totals.machines ? totals.online / totals.machines : 0} size={40} tone={offline ? 'attention' : 3} label={`${formatInt(totals.online)} of ${formatInt(totals.machines)} machines online`} />}
                onOpen={() => navigate('/machines')}
              />
            </StatStrip>

            <CapacityHistory />

            <Tabs value={tab} onValueChange={(v) => setParam('tab', v === 'machines' ? null : v)}>
              <TabsList aria-label="Capacity sections">
                {/* The count is what the tab lists: the machines (the panel below says how many are online). */}
                <TabsTrigger value="machines" icon={<Server className={TAB_ICON} />} count={totals.machines || undefined}>
                  Machines
                </TabsTrigger>
                <TabsTrigger value="rules" icon={<Clock className={TAB_ICON} />} count={state.settings.rules.length || undefined}>
                  Rules
                </TabsTrigger>
                <TabsTrigger value="pools" icon={<Boxes className={TAB_ICON} />} count={state.pools.length || undefined}>
                  Pools
                </TabsTrigger>
              </TabsList>

              <TabsContent value="machines" className="flex flex-col gap-6 pt-4">
                <Panel title="Machines" sub={`${plural(totals.machines, 'machine')}${offline ? `, ${formatInt(offline)} offline` : ''}${fleetWire !== null ? `, ${formatBytes(fleetWire, 1)}/min to the hub` : ''}`} flush>
                  <CapacityTable capacity={capacity} machines={machines} projects={projects} state={state} now={now} />
                  {capacity.length > 0 && <SlotLegend />}
                </Panel>
                <Panel
                  title="Queue"
                  sub={history ? `${formatInt(done.length)} finished` : waiting.length ? `${formatInt(waiting.length)} waiting` : undefined}
                  flush
                  actions={
                    <Segmented
                      label="Queue view"
                      value={history ? 'history' : 'waiting'}
                      onChange={(v) => setParam('queue', v === 'history' ? 'history' : null)}
                      options={[
                        { value: 'waiting', label: 'Waiting', count: waiting.length },
                        { value: 'history', label: 'History', count: done.length },
                      ]}
                    />
                  }
                >
                  <QueueList entries={history ? done : waiting} missions={missions} machines={machines} projects={projects} history={history} onQueue={() => setQueueing(true)} />
                </Panel>
              </TabsContent>
              <TabsContent value="rules" className="pt-4">
                <RulesTab state={state} capacity={capacity} machines={machinesList} projects={projectsList} now={now} />
              </TabsContent>
              <TabsContent value="pools" className="pt-4">
                <PoolsTab state={state} machines={machines} />
              </TabsContent>
            </Tabs>
            <QueueDialog open={queueing} onOpenChange={setQueueing} state={state} />
          </div>
        )}
      </DataGate>
    </PageBody>
  );
}
